/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { RootState } from 'store';

import { confirmFlightExchange } from 'actions/flights';
import CardExchangeFlights from 'components/Cards/DataCardExchangeFlights';
import FlightsExchangeConfirm from 'components/FlightsExchangeConfirm';
import FlightsExchangeJustifyConfirm, { ValueForm } from 'components/FlightsExchangeJustifyConfirm';
import TopButton from 'components/TopButton';
import FlightsSearchEdit from 'containers/FlightsSearchEdit';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { AdminUser } from 'models/adminUser';
import { FlightResultRequestOrderCriteriaEnum, FlightResultResponse } from 'models/flightSearch';
import CardLoader from 'screens/components/FlightsList/components/LoadingCard';
import { fetchUser } from 'services/userService';
import { getFormattedPrice } from 'utils/currency';

import FlightsExchangeError from './components/FlightsExchangeError';
import FlightsExchangeSuccess from './components/FlightsExchangeSuccess';
import Header from './components/Header';
import {
  Container,
  Body,
  Cards,
  NotFound,
  Wrapper,
} from './styles';
import { TravelRequested } from 'models/travelRequested';

interface Props {
  departureCode?: string;
  arrivalCode?: string;
  departureDate?: Date;
  arrivalDate?: Date;
  isLoading: boolean;
  isLoadingResults: boolean;
  changeDeparture?:boolean;
  changeArrival?:boolean;
  originalFlight?:TravelRequested;
  flightsResult: FlightResultResponse[];
  onRequest: (orderCriteria?: FlightResultRequestOrderCriteriaEnum) => void;
  onBack: () => void;
  error?: string | Error;
}

const FlightsExchangeList = ({
  flightsResult,
  isLoading,
  isLoadingResults,
  departureCode,
  arrivalCode,
  departureDate,
  arrivalDate,
  changeDeparture,
  changeArrival,
  originalFlight,
  onRequest,
  onBack,
  error,
}: Props) => {
  const history = useHistory();
  const { t } = useStoreTranslation();

  const [showEdit, setShowEdit] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState<FlightResultResponse | undefined>();
  const [showConfirm, setShowConfirm] = useState(false);

  const [isSending, setIsSending] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [breakMessage, setBreakMessage] = useState<string[]>();
  const [formData, setFormData] = useState<ValueForm>();
  const [showJustifyConfirm, setShowJustifyConfirm] = useState(false);
  const [traveler, setTraveler] = useState<AdminUser>();
  const [flights] = useState([...flightsResult]);

  const {changeFlight} = useSelector((state: RootState) => state.flights);
  const fullState = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const formattedPrice = useCallback((value: number) => getFormattedPrice({ value, currency: selectedFlight?.price?.currencyCode, t }), [selectedFlight?.price?.currencyCode, t]);
  const formattedPriceTotal = useMemo(() => selectedFlight && selectedFlight.price ? formattedPrice(selectedFlight?.price?.total).formatted : '0', [formattedPrice, selectedFlight]);

  const handleScroll = useCallback(
    (e: Event) => {
      //if (e.target !== document) return;
      if (isLoading || isLoadingResults) return;
      const hasReachedBottom = containerRef?.current?.scrollTop === (containerRef?.current?.scrollHeight - containerRef?.current?.offsetHeight)
      console.log('hasReachedBottom',hasReachedBottom);
      if (hasReachedBottom) onRequest();
    },
    [isLoading, isLoadingResults, onRequest],
  );
  // useEffect(() => {
  //   containerRef?.current?.addEventListener('scroll', handleScroll);
  //   //document.addEventListener('scroll', handleScroll);
  //   return () =>  containerRef?.current?.removeEventListener('scroll', handleScroll);
  // }, [handleScroll]);

  const handleCloseConfirm = () => {
    setShowConfirm(false);
    setShowJustifyConfirm(false);
  }

  const handleConfirm = () => {
    sendConfirm();
  }

  const handleJustifyConfirm = (values: ValueForm) => {
    setFormData(values);
    sendConfirm();
  }

  const getTraveler = (userId) => new Promise<AdminUser>(async (resolve, reject) => {
    try {
      const traveler = await fetchUser(userId);
      resolve(traveler);
    } catch (err) {
      reject(err);
    }
  });

  useEffect(() => {
    const getTravelerData = async(userId) => {
      setIsLoadingUser(true);
      try {
        const userData = await getTraveler(userId);
        if (userData) {
          setTraveler(userData);
        }
      } catch (err) {
        setHasError(true);
        setErrorMessage(String(err));
      } finally {
        setIsLoadingUser(false);
      }
    };

    const validate = async() => {
      if (traveler === undefined) return;
      if (selectedFlight === undefined) return;
      const { sectors, price, optionId } = selectedFlight;
      const justifyForBreakingRules = formData ? formData.justifyForBreakingRules : '';
      const reason = formData && formData.reason ? formData.reason : '';
      try {
        
        await dispatch(confirmFlightExchange(sectors, price, optionId, traveler, justifyForBreakingRules, reason));

        setHasError(false);
        setErrorMessage('');
        setShowSuccess(true);        
      } catch (err) {
        setHasError(true);
        setErrorMessage(String(err));
      } finally {
        setIsSending(false);
        setShowConfirm(false);
        setShowJustifyConfirm(false);
        setIsValidating(false);
      }
    };
    if (traveler && isSending && !isValidating) {
      setIsValidating(true);
      validate();
    }
    if (!isValidating && !isLoadingUser && traveler === undefined && changeFlight && changeFlight.originalFlight) {
      getTravelerData(changeFlight.originalFlight.userId);
    }
  }, [fullState, formData, isSending, isValidating, traveler, changeFlight, isLoadingUser, dispatch, selectedFlight]);

  const sendConfirm = async () => {
    setIsSending(true);
  };

  const handleClickBack = () => {
  }

  const handleEditClick = () => {
    setShowEdit(true);
    history.push('/corporate/travelRequested/exchange-edit');
  };

  const renderLoadingContent = () => (
    <Cards>
      {Number(3)
        .toArray()
        .map((number) => (
          <CardLoader
            key={number}
            col={{
              sm: 6,
              md: 6,
              lg: 6,
              xl: 6,
            }}
            roundTrip={!!arrivalDate}
          />
        ))}
    </Cards>
  );

  const renderCards = () => {
    if (!isLoading && !isLoadingResults && error !== undefined) {
      return (
        <NotFound>
          <NotFound.Icon />
          {t('flightsList.notFound')}
        </NotFound>
      );
    }

    if (!isLoading && !isLoadingResults && flightsResult.length === 0) {
      return (
        <NotFound>
          <NotFound.Icon />
          {t('flightsList.notFound')}
        </NotFound>
      );
    }

    return (
      <Cards>
        {!isLoading &&
          flightsResult.map((flight, index) => (
            <CardExchangeFlights
              key={`flight-${index}`}
              data={flight}
              testId={index === 0 ? 'flight-list-first-item' : undefined}
              onClick={() => {

              }}
              onConfirm={() => {
                setBreakMessage(flight.breakRulesMessage);
                setSelectedFlight(flight);

                if (flight.breakRulesMessage && flight.breakRulesMessage.length > 0) {
                  setShowJustifyConfirm(true);
                } else {
                  setShowConfirm(true);
                }
              }}
            />
            ))}
        {(isLoading || isLoadingResults) &&
          Number(3)
            .toArray()
            .map((number) => (
              <CardLoader
                key={number}
                col={{
                  sm: 6,
                  md: 6,
                  lg: 6,
                  xl: 6,
                }}
                roundTrip={!!arrivalDate}
              />
            ))}
      </Cards>
    );
  };

  return (
    <>
      {!isLoading && <TopButton />}

        <Header
          info={{
            departurePoint: departureCode,
            arrivalPoint: arrivalCode,
            departureDate,
            returnDate: arrivalDate,
            isOneWay: !arrivalDate,
          }}
          loading={isLoading}
          goBack={onBack}
          onEdit={handleEditClick}
        />

      <Container ref={containerRef}>
        {isLoading ? (
          <Wrapper>
            <Body>{renderLoadingContent()}</Body>
          </Wrapper>
        ) : (
          <Wrapper>
            <Body>
              {renderCards()}
            </Body>
          </Wrapper>
        )}
      </Container>

      <FlightsExchangeConfirm show={showConfirm} onClose={handleCloseConfirm} onConfirm={handleConfirm} isLoading={isSending} />

      <FlightsExchangeJustifyConfirm
        show={showJustifyConfirm}
        onClose={handleCloseConfirm}
        onConfirm={handleJustifyConfirm}
        isLoading={isSending}
        breakRulesMessage={breakMessage}
        valueTotal={String(formattedPriceTotal)} />

      <FlightsExchangeSuccess show={showSuccess} onClose={() => history.push('/corporate/travelRequested')} />

      <FlightsExchangeError
        show={hasError}
        error={errorMessage}
        onClose={() => history.push('/corporate/travelRequested')}
        onBack={() => history.push('/corporate/travelRequested')} />

      <FlightsSearchEdit show={showEdit} onClose={handleClickBack} />
    </>
  );
};

export default FlightsExchangeList;
